import "./App.scss";

import { useCallback, useState } from "react";
import { useLocation } from "react-router-dom";

import { getAccessToken } from "./auth/accessToken";
import { AuthGuard } from "./components/auth/AuthGuard";
import SessionExpiryModal from "./components/auth/SessionExpiryModal";
import { ErrorBoundary } from "./components/ErrorBoundary";
import Layout from "./components/layout/Layout";
import { useAuth } from "./contexts/useAuth";
import LoginPage from "./pages/loginPage/LoginPage";
import LogoutPage from "./pages/logoutPage/LogoutPage";
import { AppProviders } from "./providers/AppProviders";
import { DEFAULT_IDLE_TIMEOUT_SECONDS, useIdleTimeout } from "./session/useIdleTimeout";
import { AppRoutes } from "./types/enums/appRoutes";

/**
 * Splits the public login screen (full-screen, no app shell) from the
 * authenticated app, which is gated by AuthGuard. Also drives the idle
 * auto-logout for signed-in users.
 */
function AppShell() {
  const location = useLocation();
  const { isAuthenticated, silentLogout } = useAuth();
  const [sessionWarningOpen, setSessionWarningOpen] = useState(false);

  const handleIdle = useCallback(() => {
    setSessionWarningOpen(false);
    void silentLogout().catch((err) => {
      console.error("[Idle Timeout] silentLogout failed:", err);
    });
  }, [silentLogout]);

  const { reset: resetIdleTimeout } = useIdleTimeout({
    timeoutSeconds: DEFAULT_IDLE_TIMEOUT_SECONDS,
    warningSeconds: 2 * 60,
    onWarning: () => setSessionWarningOpen(true),
    onActivity: () => setSessionWarningOpen(false),
    onIdle: handleIdle,
    enabled: isAuthenticated,
  });

  const handleStaySignedIn = useCallback(() => {
    setSessionWarningOpen(false);
    resetIdleTimeout();
    void getAccessToken().catch((err) => {
      console.error("[Session Warning] token refresh failed:", err);
    });
  }, [resetIdleTimeout]);

  const handleSessionLogout = useCallback(() => {
    setSessionWarningOpen(false);
    void silentLogout().catch((err) => {
      console.error("[Session Warning] silentLogout failed:", err);
    });
  }, [silentLogout]);

  if (location.pathname === AppRoutes.login) {
    return <LoginPage />;
  }

  if (location.pathname === AppRoutes.logout) {
    return <LogoutPage />;
  }

  return (
    <AuthGuard>
      <>
        <SessionExpiryModal
          visible={sessionWarningOpen}
          onLogout={handleSessionLogout}
          onStaySignedIn={handleStaySignedIn}
        />
        <Layout />
      </>
    </AuthGuard>
  );
}

function App() {
  return (
    <AppProviders>
      <ErrorBoundary>
        <AppShell />
      </ErrorBoundary>
    </AppProviders>
  );
}

export default App;
